import { useState, useCallback, useRef } from 'react';
import { Palette, ImagePlus, Type, ArrowLeft, ArrowRight, X, Upload, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { useCard } from '@/contexts/CardContext';
import { cardThemes } from '@/lib/cardThemes';
import { presetImages } from '@/lib/presetImages';
import { generateQrDataUrl } from '@/lib/qrcode';
import { CardPreview } from '@/components/CardPreview';

export function Step2Design() {
  const { card, updateCard, setStep } = useCard();
  const frontFileRef = useRef<HTMLInputElement>(null);
  const insideFileRef = useRef<HTMLInputElement>(null);
  const [isUpdatingQr, setIsUpdatingQr] = useState(false);

  const handleFrontUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    updateCard({ photoUrl: URL.createObjectURL(file) });
  }, [updateCard]);

  const handleInsideUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    updateCard({ insideImageUrl: URL.createObjectURL(file) });
  }, [updateCard]);

  const handleThemeSelect = useCallback(async (themeId: string) => {
    const theme = cardThemes.find(t => t.id === themeId);
    if (!theme) return;
    setIsUpdatingQr(true);
    try {
      const qrDataUrl = await generateQrDataUrl(card.cashuToken, {
        width: 600,
        color: { dark: theme.qrFg, light: theme.qrBg },
      });
      updateCard({ theme, qrDataUrl });
    } catch {
      updateCard({ theme });
    } finally {
      setIsUpdatingQr(false);
    }
  }, [card.cashuToken, updateCard]);

  return (
    <div className="animate-in fade-in-0 slide-in-from-right-4 duration-500">
      <div className="text-center space-y-3 mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 shadow-lg shadow-violet-500/25">
          <Palette className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-2xl font-bold tracking-tight">Design Your Card</h2>
        <p className="text-muted-foreground max-w-md mx-auto">
          Choose images for the front cover and inside, write a message, and pick a theme.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
        {/* Left: Controls */}
        <div className="space-y-6">

          {/* ── Front Cover Image ── */}
          <ImagePicker
            label="Front Cover"
            hint="The outside of your card"
            imageUrl={card.photoUrl}
            onSelect={(url) => updateCard({ photoUrl: url })}
            onRemove={() => { updateCard({ photoUrl: null }); if (frontFileRef.current) frontFileRef.current.value = ''; }}
            onUploadClick={() => frontFileRef.current?.click()}
            presets={presetImages}
          />
          <input ref={frontFileRef} type="file" accept="image/*" onChange={handleFrontUpload} className="hidden" />

          <Separator className="bg-violet-100 dark:bg-violet-900/30" />

          {/* ── Inside Image (opposite the QR code) ── */}
          <ImagePicker
            label="Inside Image"
            hint="Opposite the QR code when opened"
            imageUrl={card.insideImageUrl}
            onSelect={(url) => updateCard({ insideImageUrl: url })}
            onRemove={() => { updateCard({ insideImageUrl: null }); if (insideFileRef.current) insideFileRef.current.value = ''; }}
            onUploadClick={() => insideFileRef.current?.click()}
            presets={presetImages}
          />
          <input ref={insideFileRef} type="file" accept="image/*" onChange={handleInsideUpload} className="hidden" />

          <Separator className="bg-violet-100 dark:bg-violet-900/30" />

          {/* ── Message ── */}
          <div className="space-y-3">
            <Label className="flex items-center gap-2 text-sm font-semibold">
              <Type className="w-4 h-4 text-violet-500" />
              Greeting Message
            </Label>
            <Textarea
              placeholder="Happy Birthday! Here's some Bitcoin for your future..."
              value={card.message}
              onChange={(e) => updateCard({ message: e.target.value })}
              className="min-h-[80px] resize-none focus:ring-2 focus:ring-violet-500/50"
              maxLength={300}
            />
            <p className="text-xs text-muted-foreground text-right">
              {card.message.length}/300
            </p>
          </div>

          {/* ── Names ── */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium">To</Label>
              <Input
                placeholder="Recipient"
                value={card.recipientName}
                onChange={(e) => updateCard({ recipientName: e.target.value })}
                className="focus:ring-2 focus:ring-violet-500/50"
                maxLength={30}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium">From</Label>
              <Input
                placeholder="Your name"
                value={card.senderName}
                onChange={(e) => updateCard({ senderName: e.target.value })}
                className="focus:ring-2 focus:ring-violet-500/50"
                maxLength={30}
              />
            </div>
          </div>

          <Separator className="bg-violet-100 dark:bg-violet-900/30" />

          {/* ── Inside Theme (for text overlay on inside image) ── */}
          <div className="space-y-3">
            <Label className="flex items-center gap-2 text-sm font-semibold">
              <Palette className="w-4 h-4 text-violet-500" />
              Message Style
            </Label>
            <div className="grid grid-cols-4 gap-2">
              {cardThemes.map((theme) => (
                <button
                  key={theme.id}
                  onClick={() => handleThemeSelect(theme.id)}
                  disabled={isUpdatingQr}
                  className={`relative h-14 rounded-lg transition-all duration-200 border-2 hover:scale-105 ${
                    card.theme.id === theme.id
                      ? 'border-violet-500 ring-2 ring-violet-500/30 scale-105'
                      : 'border-transparent hover:border-violet-300'
                  }`}
                  style={{ background: theme.bgGradient }}
                  title={theme.name}
                >
                  {card.theme.id === theme.id && (
                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-violet-500 rounded-full flex items-center justify-center">
                      <div className="w-2 h-2 bg-white rounded-full" />
                    </div>
                  )}
                </button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">{card.theme.name} &mdash; {card.theme.description}</p>
          </div>

          {/* ── Navigation ── */}
          <div className="flex gap-3 pt-2">
            <Button variant="outline" onClick={() => setStep(1)} className="flex-1">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <Button
              onClick={() => setStep(3)}
              className="flex-1 bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white shadow-lg shadow-violet-500/25"
            >
              Preview & Print
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>

        {/* Right: Live Preview */}
        <div className="space-y-3">
          <Label className="text-sm font-semibold text-center block">Live Preview</Label>
          <div className="sticky top-8">
            <CardPreview card={card} side="both" />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   Reusable image picker with presets + upload
   ───────────────────────────────────────────── */

interface ImagePickerProps {
  label: string;
  hint?: string;
  imageUrl: string | null;
  onSelect: (url: string) => void;
  onRemove: () => void;
  onUploadClick: () => void;
  presets: typeof presetImages;
}

function ImagePicker({ label, hint, imageUrl, onSelect, onRemove, onUploadClick, presets }: ImagePickerProps) {
  return (
    <div className="space-y-3">
      <div>
        <Label className="flex items-center gap-2 text-sm font-semibold">
          <ImagePlus className="w-4 h-4 text-violet-500" />
          {label}
        </Label>
        {hint && <p className="text-xs text-muted-foreground mt-0.5 ml-6">{hint}</p>}
      </div>

      {/* Current selection */}
      {imageUrl ? (
        <div className="relative rounded-xl overflow-hidden border-2 border-violet-200 dark:border-violet-800 group bg-muted/30">
          <div className="flex items-center justify-center h-36">
            <img src={imageUrl} alt={label} className="max-w-full max-h-full object-contain" />
          </div>
          <button
            onClick={onRemove}
            className="absolute top-2 right-2 p-1.5 rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/80"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <button
          onClick={onUploadClick}
          className="w-full h-28 border-2 border-dashed border-violet-300 dark:border-violet-700 rounded-xl flex flex-col items-center justify-center gap-2 hover:border-violet-500 hover:bg-violet-50/50 dark:hover:bg-violet-950/20 transition-all cursor-pointer group"
        >
          <div className="p-2 rounded-full bg-violet-100 dark:bg-violet-900/30 group-hover:scale-110 transition-transform">
            <Upload className="w-5 h-5 text-violet-500" />
          </div>
          <span className="text-sm text-muted-foreground">Upload your own photo</span>
        </button>
      )}

      {/* Preset thumbnails */}
      <div className="space-y-1.5">
        <p className="text-xs font-medium text-muted-foreground">Or choose a preset:</p>
        <div className="grid grid-cols-2 gap-2">
          {presets.map((preset) => {
            const isSelected = imageUrl === preset.url;
            return (
              <button
                key={preset.id}
                onClick={() => onSelect(preset.url)}
                className={`relative rounded-lg overflow-hidden border-2 transition-all duration-200 hover:scale-[1.02] ${
                  isSelected
                    ? 'border-violet-500 ring-2 ring-violet-500/30 shadow-lg shadow-violet-500/10'
                    : 'border-border/50 hover:border-violet-300'
                }`}
              >
                <div className="aspect-[5/7] bg-muted/30">
                  <img src={preset.url} alt={preset.name} className="w-full h-full object-cover" loading="lazy" />
                </div>
                {isSelected && (
                  <div className="absolute top-1.5 right-1.5 w-5 h-5 bg-violet-500 rounded-full flex items-center justify-center shadow-md">
                    <Check className="w-3 h-3 text-white" />
                  </div>
                )}
                <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent px-2 py-1.5">
                  <span className="text-[10px] font-semibold text-white leading-tight">{preset.name}</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Upload different button when something is selected */}
      {imageUrl && (
        <Button variant="outline" size="sm" onClick={onUploadClick} className="w-full text-xs">
          <Upload className="w-3.5 h-3.5 mr-1.5" />
          Upload a different photo
        </Button>
      )}
    </div>
  );
}
